import { IMG } from "./images";

// Only services we have authentic Last Cut Landscaping photos for.
// Services without real photo evidence are intentionally hidden until photos are available.
export const services = [
  {
    slug: "artificial-grass",
    name: "Artificial Grass Installation",
    short: "Hyper-realistic artificial grass that stays lush 365 days a year — perfect for Scotland's weather.",
    image: IMG.artificialGrass,
    intro: "Scotland's wet, unpredictable weather is brutal on real lawns. Our premium artificial grass gives you a perfect, dog-friendly, child-safe garden you can actually use — without the mud, the patches or the endless mowing.",
    benefits: [
      "All-weather usability — no more muddy patches after Scottish downpours",
      "Pet & child friendly with permeable drainage backing",
      "Zero mowing, watering or fertilising — reclaim your weekends",
      "10–15 year manufacturer warranty on every install",
      "Hyper-realistic multi-tone fibres indistinguishable from real grass",
      "Sustainably backed with full sub-base preparation",
    ],
    process: [
      { step: "Free Site Survey", detail: "We visit your garden, take accurate measurements, assess the existing ground conditions, and discuss your requirements and budget." },
      { step: "Ground Preparation", detail: "Existing turf and unsuitable material are excavated to approximately 100–125mm (or deeper where ground conditions require) to create a solid foundation." },
      { step: "Sub-base Installation", detail: "Install and compact 75–100mm of MOT Type 1 in layers, followed by a 20–25mm granite dust laying course, screeded to create a smooth, stable and free-draining surface." },
      { step: "Weed Membrane", detail: "A heavy-duty weed membrane is installed where appropriate to help suppress weed growth and improve the long-term performance of the installation." },
      { step: "Grass Installation", detail: "Premium artificial grass is carefully laid, trimmed, seamlessly joined and securely fixed using professional installation methods." },
      { step: "Finishing Touches", detail: "The lawn is power brushed to lift the fibres and create a natural appearance. Kiln-dried sand is applied where appropriate for the selected artificial grass product." },
    ],
    faqs: [
      { q: "How much does artificial grass cost in Scotland?", a: "Typical installations range from £65–£95 per m² fully fitted, including ground preparation. Final pricing depends on access, ground conditions and the grass specification you choose." },
      { q: "Is artificial grass good for dogs?", a: "Yes — our grass is permeable, antibacterial backed and rinses clean with water. It's actually more hygienic than real grass for most dog owners." },
      { q: "Does artificial grass drain in heavy Scottish rain?", a: "Absolutely. With a properly prepared sub-base, our grass drains around 60 litres per m² per minute — far more than Scotland's heaviest downpour." },
      { q: "How long does artificial grass last?", a: "Our installs come with a 10–15 year manufacturer warranty and typically last 18–20 years with light maintenance." },
    ],
  },
  {
    slug: "patios",
    name: "Patios",
    short: "Indian sandstone, porcelain & natural stone patios built to last Scottish winters.",
    image: IMG.patios,
    intro: "A premium patio transforms how you use your garden. We design and install Indian sandstone, porcelain and natural stone patios with proper sub-base, drainage and pointing — built to handle decades of Scottish frost.",
    benefits: [
      "Premium Indian sandstone, porcelain & granite options",
      "Full concrete sub-base — no sand-laid shortcuts that fail in frost",
      "Slurry-primed and brush-in pointing for long-term durability",
      "Bespoke patterns, edging and step details",
      "Drainage built in to handle Scottish rainfall",
      "2-year workmanship guarantee",
    ],
    process: [
      { step: "Design consultation", detail: "Stone selection, pattern layout, level planning and drainage strategy." },
      { step: "Excavation", detail: "Existing surface removed to a depth of 150–200mm depending on use." },
      { step: "Sub-base", detail: "Compacted Type 1 MOT to 100mm minimum." },
      { step: "Mortar bed", detail: "Full bed of 6:1 sharp sand and cement, slurry primed for permanent adhesion." },
      { step: "Stone laying", detail: "Each slab levelled to fall, with consistent joints maintained." },
      { step: "Jointing & sealing", detail: "Brush-in compound for crack-resistant joints, optional sealing." },
    ],
    faqs: [
      { q: "Which patio stone is best for Scotland?", a: "Porcelain is the most frost-resistant and low-maintenance choice. Indian sandstone is the most popular for natural character. We'll recommend based on your budget and usage." },
      { q: "How much does a patio cost in Scotland?", a: "Most patios range from £120–£180 per m² supplied and laid. Pricing depends on stone choice, complexity and access." },
      { q: "Will my patio crack in winter?", a: "Not if it's installed correctly. Our full mortar bed and slurry primer system prevents the freeze-thaw failure that plagues sand-laid patios." },
    ],
  },
  {
    slug: "decking",
    name: "Decking",
    short: "Composite & hardwood decking — slip-resistant, low-maintenance, built for Scottish weather.",
    image: IMG.decking,
    intro: "Decking adds usable outdoor living space that works year-round. We install premium composite and treated softwood decking with hidden fixings, slip-resistant grooves and a structural sub-frame engineered to last.",
    benefits: [
      "Composite (Trex, Millboard) and hardwood options",
      "Slip-resistant grooved surface — essential in Scottish rain",
      "Hidden fixing systems for a clean finish",
      "Pressure-treated structural sub-frame with C24 joists",
      "Built-in LED lighting & balustrade options",
      "Decades of life on composite, 15+ years on softwood",
    ],
    process: [
      { step: "Design", detail: "Layout, level, finished height and material selection." },
      { step: "Ground works", detail: "Concrete pads or ground screws for a permanent base." },
      { step: "Sub-frame", detail: "C24 timber joists at 400mm centres with cross-bracing." },
      { step: "Decking install", detail: "Boards fixed with hidden clips or screwed and plugged." },
      { step: "Finishing", detail: "Fascia boards, balustrade, steps and post caps installed." },
    ],
    faqs: [
      { q: "Composite vs wood decking — which is better in Scotland?", a: "Composite wins for low maintenance and longevity in our wet climate. Wood is cheaper upfront but requires annual oiling and won't last as long without it." },
      { q: "Does decking get slippery in Scottish weather?", a: "Quality grooved composite stays grippy in the wet. We never install smooth-surface boards for walking areas." },
      { q: "Do I need planning permission for a deck?", a: "Generally no, as long as it's under 30cm high and doesn't cover more than 50% of your garden. We'll advise on your specific site." },
    ],
  },
  {
    slug: "fencing",
    name: "Fencing",
    short: "Closed-board, feather-edge & decorative fencing — installed with concrete-set posts to outlast Scottish gales.",
    image: IMG.fencing,
    intro: "Fencing is the backbone of garden privacy, security and curb appeal. We install closed-board, feather-edge, slatted contemporary and decorative fencing — all with concrete-set posts and pressure-treated timber that survives Scottish winters.",
    benefits: [
      "Concrete-set posts — no rotting, no leaning",
      "Pressure-treated timber rated for 15+ years",
      "Closed-board, feather-edge, slatted and decorative options",
      "Gates, trellis tops and post caps",
      "Repairs, replacements and new installs",
      "Tidy site & all waste removed",
    ],
    process: [
      { step: "Site survey", detail: "Line marking, level check, neighbour considerations." },
      { step: "Post setting", detail: "600mm deep post holes filled with Postcrete." },
      { step: "Panel install", detail: "Panels fitted with capping rails and gravel boards." },
      { step: "Finish", detail: "Post caps, gate hardware and optional treatment coat." },
    ],
    faqs: [
      { q: "How tall can my fence be in Scotland?", a: "2m at the back, 1m to the front without planning. We'll confirm based on your property." },
      { q: "How long do fences last in Scottish weather?", a: "Concrete-set, pressure-treated fences last 15–20 years with light maintenance. Cheap fences set in soil typically fail within 5 years." },
    ],
  },
  {
    slug: "garden-design",
    name: "Garden Design",
    short: "Full garden design service — from concept and plant scheme through to complete build.",
    image: IMG.gardenDesign,
    intro: "We design gardens that look stunning, work for your lifestyle and thrive in Scotland's climate. From compact courtyards to landscaped acres, we produce CAD plans, plant schedules and execute the build end-to-end.",
    benefits: [
      "Full CAD garden plans & visualisations",
      "Climate-appropriate planting schemes for Scotland",
      "Hardscape, planting & lighting integrated",
      "Family, low-maintenance, wildlife or formal styles",
      "Single contractor — design through to build",
      "Lifetime support on planting questions",
    ],
    faqs: [
      { q: "How much does a garden design cost?", a: "Design-only packages start at £495. Full design-and-build projects typically range from £8,000 to £75,000+ depending on scale and specification." },
      { q: "Which plants work in Scottish gardens?", a: "We work with Scotland-hardy planting — hydrangeas, hostas, ferns, heathers, ornamental grasses and a curated selection of evergreens for year-round structure." },
    ],
  },
  {
    slug: "garden-makeovers",
    name: "Garden Makeovers",
    short: "Complete garden transformations — from neglected plot to your favourite room of the house.",
    image: IMG.gardenMakeovers,
    intro: "Garden makeovers are our signature work. We take tired, neglected or unloved gardens and transform them into beautiful, usable spaces — combining hardscape, planting, lawns and lighting in one coordinated project.",
    benefits: [
      "Complete design-and-build packages",
      "All trades coordinated under one contractor",
      "Fixed-price quotes, no surprises",
      "Before/after photo journey",
      "Staged payment plans available",
    ],
    faqs: [
      { q: "How long does a garden makeover take?", a: "Small gardens 1–2 weeks, medium 3–4 weeks, large transformations 6–10 weeks." },
      { q: "Do you do removals and waste disposal?", a: "Yes — fully licensed waste carriers, all clearance included in our quotes." },
    ],
  },
  {
    slug: "commercial-landscaping",
    name: "Commercial Landscaping",
    short: "Grounds maintenance, soft & hard landscaping for commercial sites across Scotland.",
    image: IMG.commercial,
    intro: "We work with property managers, developers, hotels and offices across Scotland providing one-off landscaping projects and ongoing grounds maintenance. Insured to £5m, fully compliant and reliable.",
    benefits: [
      "£5m public liability insurance",
      "CHAS accredited contractors",
      "Scheduled grounds maintenance contracts",
      "One-off commercial install projects",
      "RAMS and CDM compliance",
    ],
    faqs: [
      { q: "Do you take on grounds maintenance contracts?", a: "Yes — monthly, fortnightly and weekly schedules available across the Central Belt." },
    ],
  },
];

export const findService = (slug) => services.find((s) => s.slug === slug);
